import { ReactNode } from "react";

import {
  DealTermsTableLayout,
  ParagraphSectionLayout,
  ParagraphTitle,
} from "@frontend/common";

import { getAxisRoleLabel, useAxisRoleQuery } from "@api/axisRole/axisRole";
import { DEALS_PARAGRAPH_CODE } from "@EditArticleContainer/shared/constants/editArticle.constants";
import { useCreateCompanyLogoComponentList } from "@EditArticleContainer/shared/hooks/useCreateCompanyLogoComponentList";
import { useParagraphEditedData } from "@EditArticleContainer/shared/hooks/useParagraph";
import { useUpdateParagraph } from "@EditArticleContainer/shared/hooks/useUpdateParagraph";
import {
  REF_CODE_DEAL_PARTNER_A_NAME,
  REF_CODE_DEAL_PARTNER_A_RESPONSIBILITY,
  REF_CODE_DEAL_PARTNER_B_NAME,
  REF_CODE_DEAL_PARTNER_B_RESPONSIBILITY,
  REF_CODE_DEAL_PARTNER_C_NAME,
  REF_CODE_DEAL_PARTNER_C_RESPONSIBILITY,
} from "@EditArticleContainer/shared/libs/refCode/constants/refCode.constants";
import { createEditArticleCrossLicenseShellList } from "@EditArticleContainer/shared/utils/createEditArticleShellList";

import * as S from "./DealsParagraphPartnerAxisLASC.style";
import {
  partnerANameLabels,
  partnerANameText,
  partnerAResponsibilityLabels,
  partnerAResponsibilityText,
  partnerBNameLabels,
  partnerBNameText,
  partnerBResponsibilityLabels,
  partnerBResponsibilityText,
  partnerCNameLabels,
  partnerCNameText,
  partnerCResponsibilityLabels,
  partnerCResponsibilityText,
} from "./utils/getPartnerAxisLASCShellValue";

const PARAGRAPH_TITLE = "Deal Terms";

const DealsParagraphPartnerAxisLASC = () => {
  const paragraph = useParagraphEditedData(
    DEALS_PARAGRAPH_CODE.partnerAxisLASC,
  );
  const [updateParagraph, loadingIndex, updateKeyMap] = useUpdateParagraph({
    paragraphCode: DEALS_PARAGRAPH_CODE.partnerAxisLASC,
    sectionCode: "TY002",
  });
  const { createCompanyLogoComponentList } =
    useCreateCompanyLogoComponentList();
  const { data: axisRole } = useAxisRoleQuery();

  if (!paragraph) return null;

  // 타이틀을 "Partner A (Axis Role)" 형태로 구성한다. Role이 없으면 라벨만.
  const toPartnerTitle = (label: string, axis: string) => {
    const role = getAxisRoleLabel(axisRole, axis);
    if (!role) return label;
    return (
      <>
        {label}
        <S.TitleAxisRole>({role})</S.TitleAxisRole>
      </>
    );
  };

  const partnerAName = partnerANameText(paragraph);
  const partnerBName = partnerBNameText(paragraph);
  const partnerCName = partnerCNameText(paragraph);

  const partnerAResponsibility = partnerAResponsibilityText(paragraph);
  const partnerBResponsibility = partnerBResponsibilityText(paragraph);
  const partnerCResponsibility = partnerCResponsibilityText(paragraph);

  const buildPartnerColumn = (
    title: ReactNode,
    nameText: string,
    nameLabels: ReturnType<typeof partnerANameLabels>,
    nameRefKey: string,
    responsibilityText: string,
    responsibilityLabels: ReturnType<typeof partnerAResponsibilityLabels>,
    responsibilityRefKey: string,
  ) => {
    if (!nameText && !responsibilityText) return [];
    return createEditArticleCrossLicenseShellList([
      {
        title,
        text: nameText,
        labels: nameLabels,
        refKey: nameRefKey,
        imagePosition: "left" as const,
        imageNodeList: createCompanyLogoComponentList(
          nameText.replaceAll("- ", "").split("\n"),
        ),
        isDisabled: true,
      },
      {
        title: "Responsibility & Rights",
        text: responsibilityText,
        labels: responsibilityLabels,
        refKey: responsibilityRefKey,
      },
    ]);
  };

  const partnerAColumn = buildPartnerColumn(
    toPartnerTitle("Partner A", "A"),
    partnerAName,
    partnerANameLabels(paragraph),
    REF_CODE_DEAL_PARTNER_A_NAME,
    partnerAResponsibility,
    partnerAResponsibilityLabels(paragraph),
    REF_CODE_DEAL_PARTNER_A_RESPONSIBILITY,
  );

  const partnerBColumn = buildPartnerColumn(
    toPartnerTitle("Partner B", "B"),
    partnerBName,
    partnerBNameLabels(paragraph),
    REF_CODE_DEAL_PARTNER_B_NAME,
    partnerBResponsibility,
    partnerBResponsibilityLabels(paragraph),
    REF_CODE_DEAL_PARTNER_B_RESPONSIBILITY,
  );

  const partnerCColumn = buildPartnerColumn(
    toPartnerTitle("Partner C", "C"),
    partnerCName,
    partnerCNameLabels(paragraph),
    REF_CODE_DEAL_PARTNER_C_NAME,
    partnerCResponsibility,
    partnerCResponsibilityLabels(paragraph),
    REF_CODE_DEAL_PARTNER_C_RESPONSIBILITY,
  );

  const tableItems = [partnerAColumn, partnerBColumn, partnerCColumn].filter(
    (col) => col.length > 0,
  );

  if (tableItems.length === 0) return null;

  return (
    <ParagraphSectionLayout>
      <ParagraphTitle title={PARAGRAPH_TITLE} />
      <S.ParagraphPartnerAxisLASCWrapper key={updateKeyMap.get(0)}>
        <S.ParagraphPartnerAxisLASCUpdateButton
          disabled={loadingIndex === 0}
          onClick={() => updateParagraph(0)}
        />
        <DealTermsTableLayout items={tableItems} isEditable />
      </S.ParagraphPartnerAxisLASCWrapper>
    </ParagraphSectionLayout>
  );
};

export default DealsParagraphPartnerAxisLASC;
